import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { fmtAbv } from '../utils/format.js';
import { uiConfirm } from '../utils/dialogs.js';

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' }, info: { background: '#2563eb', color: '#fff' } };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 580, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>{children}</div>;
}
function Input(props) { return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>; }
function Textarea(props) { return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />; }
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function BtnRow({ children }) { return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>; }
function Err({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p> : null; }

// ── Status colours ────────────────────────────────────────────────────────────
const STATUS_C = {
  draft:     { bg: '#f3f4f6', color: '#6b7280' },
  sent:      { bg: '#dbeafe', color: '#1e40af' },
  accepted:  { bg: '#dcfce7', color: '#166534' },
  rejected:  { bg: '#fee2e2', color: '#991b1b' },
  expired:   { bg: '#ede9fe', color: '#5b21b6' },
  converted: { bg: '#fef9c3', color: '#854d0e' },
};

function StatusBadge({ status }) {
  const c = STATUS_C[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.color }}>{status}</span>;
}

function fmt(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '—'; }
function fmtMoney(v) { return v != null ? `£${Number(v).toFixed(2)}` : '—'; }

const QUOTE_TYPES = ['contract_distillation', 'contract_bottling', 'storage', 'full_service', 'other'];
const STATUS_FLOW = { draft: ['sent', 'rejected'], sent: ['accepted', 'rejected'], accepted: ['converted'], rejected: [], expired: [], converted: [] };

// ── Create Quote modal ────────────────────────────────────────────────────────
function CreateQuoteModal({ onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  const { data: recipeData } = useApi('/recipes');
  const recipes = recipeData?.recipes ?? recipeData ?? [];

  const [form, setForm] = useState({
    client_id: '', quote_type: 'contract_distillation', recipe_id: '',
    target_volume_litres: '', target_abv: '', target_bottle_size_ml: '', target_units: '',
    margin_pct: '30', valid_until: '', notes: '',
  });
  const [lines, setLines] = useState([{ description: '', quantity: '', unit_cost: '', unit_price: '', stock_item_id: '' }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function addLine() { setLines(ls => [...ls, { description: '', quantity: '', unit_cost: '', unit_price: '', stock_item_id: '' }]); }
  function removeLine(i) { setLines(ls => ls.filter((_, idx) => idx !== i)); }
  function setLine(i, k, v) { setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l)); }

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/quotes', {
        method: 'POST',
        body: {
          ...form,
          recipe_id: form.recipe_id || undefined,
          target_volume_litres: form.target_volume_litres ? parseFloat(form.target_volume_litres) : undefined,
          target_abv: form.target_abv ? parseFloat(form.target_abv) : undefined,
          target_bottle_size_ml: form.target_bottle_size_ml ? parseInt(form.target_bottle_size_ml) : undefined,
          target_units: form.target_units ? parseInt(form.target_units) : undefined,
          margin_pct: form.margin_pct ? parseFloat(form.margin_pct) : undefined,
          valid_until: form.valid_until || undefined,
          lines: lines.filter(l => l.description).map((l, i) => ({ ...l, quantity: parseFloat(l.quantity) || 0, unit_cost: parseFloat(l.unit_cost) || 0, unit_price: parseFloat(l.unit_price) || 0, sort_order: i })),
        },
      });
      onSaved();
    } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="New Quote" onClose={onClose} width={680}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Grid2>
          <Field label="Client *"><Select value={form.client_id} onChange={set('client_id')} required><option value="">Select client...</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Quote type *"><Select value={form.quote_type} onChange={set('quote_type')}>{QUOTE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}</Select></Field>
        </Grid2>
        <Field label="Recipe (if applicable)"><Select value={form.recipe_id} onChange={set('recipe_id')}><option value="">No recipe</option>{recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></Field>
        <Grid2>
          <Field label="Target volume (litres)"><Input type="number" min="0" step="0.1" value={form.target_volume_litres} onChange={set('target_volume_litres')} placeholder="e.g. 1000" /></Field>
          <Field label="Target ABV %"><Input type="number" min="0" max="100" step="0.1" value={form.target_abv} onChange={set('target_abv')} placeholder="e.g. 40" /></Field>
        </Grid2>
        <Grid2>
          <Field label="Bottle size (ml)"><Input type="number" value={form.target_bottle_size_ml} onChange={set('target_bottle_size_ml')} placeholder="e.g. 700" /></Field>
          <Field label="Target units"><Input type="number" value={form.target_units} onChange={set('target_units')} placeholder="e.g. 500" /></Field>
        </Grid2>
        <Grid2>
          <Field label="Margin %"><Input type="number" min="0" max="100" step="0.1" value={form.margin_pct} onChange={set('margin_pct')} placeholder="e.g. 30" /></Field>
          <Field label="Valid until"><Input type="date" value={form.valid_until} onChange={set('valid_until')} /></Field>
        </Grid2>

        {/* Lines */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Quote lines</label>
            <Btn size="sm" variant="ghost" type="button" onClick={addLine}>+ Add line</Btn>
          </div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <Input value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Description" />
              <Input type="number" min="0" value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} placeholder="Qty" />
              <Input type="number" min="0" step="0.01" value={l.unit_cost} onChange={e => setLine(i, 'unit_cost', e.target.value)} placeholder="Cost £" />
              <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} placeholder="Price £" />
              <button type="button" onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18, lineHeight: 1 }}>&#215;</button>
            </div>
          ))}
        </div>

        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Quote'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Quote detail ──────────────────────────────────────────────────────────────
function QuoteDetail({ quoteId, onBack, onRefresh }) {
  const { data, loading, refetch } = useApi(`/quotes/${quoteId}`, [quoteId]);
  const quote = data ?? null;
  const [acting, setActing] = useState(false);
  const [actErr, setActErr] = useState('');

  async function changeStatus(newStatus) {
    setActing(true); setActErr('');
    try { await apiFetch(`/quotes/${quoteId}/status`, { method: 'PATCH', body: { status: newStatus } }); refetch(); onRefresh(); }
    catch(e) { setActErr(e.message); } finally { setActing(false); }
  }

  async function convert() {
    if (!(await uiConfirm('Convert this accepted quote to a production run?'))) return;
    setActing(true); setActErr('');
    try {
      const res = await apiFetch(`/quotes/${quoteId}/convert`, { method: 'POST', body: {} });
      onRefresh();
      const runRef = res?.data?.run_ref || res?.data?.production_run?.run_ref;
      if (runRef) alert(`Converted to production run: ${runRef}`);
      refetch();
    } catch(e) { setActErr(e.message); } finally { setActing(false); }
  }

  if (loading || !quote) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  const nextStatuses = STATUS_FLOW[quote.status] || [];
  const totalPrice = quote.lines?.reduce((sum, l) => sum + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0) ?? 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>{quote.quote_ref}</h2>
            <StatusBadge status={quote.status} />
            {quote.ai_generated && <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#ede9fe', color: '#5b21b6' }}>AI</span>}
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {quote.client_name} · {quote.quote_type?.replace(/_/g,' ')} · Created {fmt(quote.created_at)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {nextStatuses.map(s => (
            <Btn key={s} size="sm" variant={s === 'rejected' ? 'danger' : s === 'accepted' ? 'primary' : 'ghost'} onClick={() => changeStatus(s)} disabled={acting}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Btn>
          ))}
          {quote.status === 'accepted' && <Btn size="sm" variant="info" onClick={convert} disabled={acting}>Convert to Run</Btn>}
        </div>
      </div>

      {actErr && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{actErr}</p>}

      {/* Info cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Recipe', value: quote.recipe_name || '—' },
          { label: 'Volume target', value: quote.target_volume_litres ? `${quote.target_volume_litres}L` : '—' },
          { label: 'ABV target', value: quote.target_abv ? `${fmtAbv(quote.target_abv)}%` : '—' },
          { label: 'Bottle size', value: quote.target_bottle_size_ml ? `${quote.target_bottle_size_ml}ml` : '—' },
          { label: 'Target units', value: quote.target_units ?? '—' },
          { label: 'Margin', value: quote.margin_pct ? `${quote.margin_pct}%` : '—' },
          { label: 'Valid until', value: fmt(quote.valid_until) },
          { label: 'Total price', value: fmtMoney(quote.total_amount ?? totalPrice) },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
          </div>
        ))}
      </div>

      {quote.notes && <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{quote.notes}</div>}

      {/* Lines */}
      {quote.lines?.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Quote lines</h3>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Description', 'Qty', 'Unit cost', 'Unit price', 'Total'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quote.lines.map((l, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px' }}>{l.description}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{l.quantity}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtMoney(l.unit_cost)}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{fmtMoney(l.unit_price)}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700 }}>{fmtMoney((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0))}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                  <td colSpan={4} style={{ padding: '10px 14px', fontWeight: 700, textAlign: 'right' }}>Total</td>
                  <td style={{ padding: '10px 14px', fontWeight: 800 }}>{fmtMoney(quote.total_amount ?? totalPrice)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Converted run */}
      {quote.converted_run_ref && (
        <div style={{ marginTop: 20, padding: '12px 16px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 'var(--radius)', fontSize: 13 }}>
          Converted to production run: <strong style={{ fontFamily: 'monospace' }}>{quote.converted_run_ref}</strong>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted'];

export default function Quotes() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
  const { data, loading, refetch } = useApi(`/quotes${qs}`, [statusFilter]);
  const quotes = data?.quotes ?? data ?? [];

  if (selectedId) return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 8px' }}>
      <QuoteDetail quoteId={selectedId} onBack={() => setSelectedId(null)} onRefresh={refetch} />
    </div>
  );

  const today = new Date();

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      {showCreate && <CreateQuoteModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Quotes</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Contract client quoting — create, send, and convert to production runs</p>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ New Quote</Btn>
      </div>

      {/* Status filters */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', ...STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s}</button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : quotes.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>No quotes found</p>
          <p style={{ fontSize: 13, margin: 0 }}>Create your first quote above.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Ref', 'Client', 'Type', 'Recipe', 'Volume', 'Total', 'Margin', 'Valid until', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotes.map(q => {
                const expired = q.valid_until && new Date(q.valid_until) < today && q.status === 'sent';
                return (
                  <tr key={q.id} onClick={() => setSelectedId(q.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '11px 14px', fontWeight: 700, fontFamily: 'monospace' }}>{q.quote_ref}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 600 }}>{q.client_name}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--text-muted)', fontSize: 12 }}>{q.quote_type?.replace(/_/g,' ')}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{q.recipe_name || '—'}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{q.target_volume_litres ? `${q.target_volume_litres}L` : '—'}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 600 }}>{fmtMoney(q.total_amount)}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{q.margin_pct ? `${q.margin_pct}%` : '—'}</td>
                    <td style={{ padding: '11px 14px', color: expired ? '#dc2626' : 'var(--text-muted)', fontWeight: expired ? 700 : 400 }}>{fmt(q.valid_until)}{expired && ' ⚠'}</td>
                    <td style={{ padding: '11px 14px' }}><StatusBadge status={q.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
